import { useMemo } from 'react'
import { useQuery } from '@connectrpc/connect-query'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { authTransport } from '@jotta/grpc-connect-client/transport'
import { getSupportedPaymentMethods } from '@jotta/grpc-connect-openapi/subscriptionQuery'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '../../Accordion/Accordion'
import { Button } from '../../Buttons/Button/Button'
import {
  PaymentMethodOption,
  paymentMethodOptions,
} from '../../Elements/PaymentSelector/paymentMethodOptions'
import {
  PaymentRadio,
  usePaymentMethodMutation,
} from '../../Elements/PaymentSelector/PaymentRadio'
import { StripeCardForm } from '../../Elements/PaymentSelector/Stripe'
import SvgWarningCircle from '../../generated/icons/SvgWarningCircle'
import SvgX from '../../generated/icons/SvgX'
import { PlainBrandIcon } from '../../Icons/BrandIcon/BrandIcon'
import { LoadingOverlay } from '../../Layout/LoadingOverlay/LoadingOverlay'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../Dialog/Dialog'

export function ChangePaymentMethodDialog({
  isOpen,
  onOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  onSuccess: () => void
}) {
  const {
    paymentMethod,
    setPaymentMethod,
    error,
    clearError,
    stripeError,
    setStripeError,
    onChangePaymentMethod,
    isPending,
    pendingPayPal,
    elements,
  } = usePaymentMethodMutation({
    onSuccess: () => {
      onClose()
      onSuccess()
    },
    onOpen: onOpen,
  })

  const { data: getSupportedPaymentMethodsResponse } = useQuery(
    getSupportedPaymentMethods,
    {},
    {
      transport: authTransport,
    },
  )

  const paymentMethods = useMemo(
    () =>
      getSupportedPaymentMethodsResponse?.paymentMethods &&
      paymentMethodOptions(getSupportedPaymentMethodsResponse?.paymentMethods),
    [getSupportedPaymentMethodsResponse],
  )

  if (pendingPayPal) {
    return (
      <Dialog open>
        <DialogContent>
          <DialogHeader showCloseButton={false}>
            <DialogTitle>
              <Trans>Update payment details</Trans>
            </DialogTitle>
          </DialogHeader>
          <LoadingOverlay id="ChangePaymentMethodDialog" open />
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open) {
          onClose()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Update payment details</Trans>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <PaymentRadio
            paymentMethods={paymentMethods}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
          />
          <Accordion
            type="single"
            disabled
            value={
              paymentMethod === PaymentMethodOption.STRIPE_CARD
                ? 'open'
                : 'closed'
            }
          >
            <AccordionItem value="open" className="border-0">
              <AccordionContent className="px-1">
                <StripeCardForm
                  onVerify={_ => {
                    onChangePaymentMethod()
                  }}
                  errors={stripeError}
                  setErrors={setStripeError}
                  elements={elements}
                  className="pt-6 text-primary"
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          {error !== undefined && (
            <div className="pt-4">
              <div
                role="alert"
                className="alert alert-danger grid grid-cols-[24px_auto_20px] gap-[10px]"
              >
                <PlainBrandIcon
                  icon={SvgWarningCircle}
                  height={24}
                  width={24}
                  className="flex-shrink-0 text-danger"
                />
                <div>
                  <header className="alert-title">
                    <Trans>We're sorry! Something went wrong</Trans>
                  </header>
                  <p className="body-sm-ln">{error}</p>
                </div>
                <Button
                  className="btn btn-ghost size-5 p-0 hover:bg-transparent"
                  icon={SvgX}
                  onClick={clearError}
                  aria-label={t`Close`}
                />
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            className="btn btn-primary"
            loading={isPending}
            disabled={!paymentMethods?.length || paymentMethod === undefined}
            onClick={_ => {
              onChangePaymentMethod()
              clearError()
            }}
          >
            <Trans>Confirm</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
